import React, { FunctionComponent, ReactNode, useEffect, useState } from 'react';
import { Icon } from '../../icons/icon/icon-component';
import Pill from '../../pill/pill-component';
import styles from './checkbox-component.module.scss';

type CheckboxProps = {
  name: string;
  /** Array of string | number of selected keys */
  defaultSelection?: (string | number)[];
  /** Array of {key: string | number, element} objects */
  list?: {
    key: string | number;
    elt: ReactNode;
    description?: ReactNode;
    isDisabled?: boolean;
  }[];
  /** in rem */
  spacing?: number;
  form?: 'default' | 'pill';
  isDisabled?: boolean;
  isPillClosable?: boolean;
  pillCloseCallback?: (e: React.SyntheticEvent) => void;
  /** (key: string | number) => void */
  selectCallback?: (keys: (string | number)[]) => void;
  className?: string;
  inline?: boolean;
  /** Sets the checkbox id as the `name` prop (without the `key_` prefix). Can only be set to true when 1 checkbox is used to avoid duplicate ids */
  sameIdAsName?: boolean;
};

/**
 * Checkbox component
 *
 * https://www.figma.com/file/jiN4FkdkhDZIWxIkPS6Ru7/design-system?node-id=33%3A1930
 */
export const Checkbox: FunctionComponent<CheckboxProps> = ({
  defaultSelection,
  form = 'default',
  isDisabled = false,
  isPillClosable = false,
  inline = true,
  ...props
}) => {
  const [selectedKeys, setSelectedKeys] = useState(defaultSelection);
  const [lastDefaultSelectionProp, setLastDefaultSelectionProp] = useState(defaultSelection);
  const [closedPills, setClosedPills] = useState<(number | string)[]>([]);

  const styleSpacing: React.CSSProperties = (
    props.spacing
      ? {
          //spacing between checkboxes
          '--checkbox-spacing': props.spacing + 'rem',
        }
      : {}
  ) as React.CSSProperties;

  //setting values as the default selection
  if (defaultSelection !== lastDefaultSelectionProp) {
    setSelectedKeys(defaultSelection);
    setLastDefaultSelectionProp(defaultSelection);
  }

  useEffect(() => {
    if (props.sameIdAsName && props.list?.length && props.list.length > 1) {
      throw new TypeError("Cannot use flag 'sameIdAsName' with multiple checkboxes!");
    }
  }, [props.list, props.sameIdAsName]);

  return (
    <div style={styleSpacing} className={`${styles['checkbox-component']} ${inline ? styles.inline : ''} ${props.className ?? ''}`}>
      {props.list &&
        props.list.map((item) => {
          const selected = selectedKeys?.filter((x) => x === item.key).length === 1;
          const closed = form === 'pill' && closedPills?.filter((x) => x === item.key).length > 0;

          //to ensure it does not interfere with other checkbox group having same keys
          const id = props.sameIdAsName ? props.name : item.key.toString() + '_' + props.name;

          return form === 'pill' ? (
            <div
              tabIndex={0}
              id={id}
              data-testid={id}
              key={item.key}
              className={`${styles['pill-wrapper']} ${closed ? styles['pill-close'] : ''}`}
              onClick={() => !item.isDisabled && onSelect(item.key)}
              onKeyDown={(e) => (!item.isDisabled && e.key === 'Enter' ? onSelect(item.key) : '')}
              aria-disabled={isDisabled || item.isDisabled}
            >
              <Pill
                isDisabled={isDisabled || item.isDisabled}
                color='white'
                children={<span className={styles['label-text']}>{item.elt}</span>}
                isClosable={isPillClosable}
                closeCallback={(e) => onPillClose(e, item.key)}
                class={`${styles.pill} ${selected ? styles['selected-pill'] : styles['unselected-pill']} ${
                  isPillClosable ? styles['pill-closable'] : ''
                }`}
              ></Pill>
            </div>
          ) : (
            <div>
              <div
                tabIndex={0}
                key={item.key}
                className={`${styles.container}`}
                style={{ opacity: isDisabled || item.isDisabled ? 0.5 : 1 }}
                onKeyDown={(e) => (!item.isDisabled && e.key === 'Enter' ? onSelect(item.key) : '')}
                aria-disabled={isDisabled || item.isDisabled}
              >
                <input
                  name={props.name}
                  id={id}
                  data-testid={id}
                  type='checkbox'
                  className={`${styles['checkbox-custom']} ${selected && styles['checkbox-custom-selected']}`}
                  value={item.key}
                  checked={selected}
                  onChange={() => !item.isDisabled && onSelect(item.key)}
                />
                <label htmlFor={id} className={styles['checkbox-custom-label']}>
                  <div className={selected ? styles['checkmark-selected'] : styles['checkmark-unselected']}>
                    <Icon name='check'></Icon>
                  </div>
                  <span className={styles['label-text']}>{item.elt}</span>
                </label>
              </div>
              {item.description && item.description}
            </div>
          );
        })}
    </div>
  );

  //select or unselect checkbox
  function select(key: string | number) {
    let newSelections: (string | number)[];

    if (selectedKeys?.some((x) => x === key)) {
      newSelections = selectedKeys?.filter((x) => x !== key) ?? [];
    } else {
      newSelections = (selectedKeys ?? []).concat(key);
    }
    setSelectedKeys(newSelections);

    props.selectCallback?.(newSelections);
  }

  function onSelect(key: string | number) {
    if (!isDisabled) {
      select(key);
    }
    return;
  }

  function onPillClose(e: React.SyntheticEvent, key: string | number) {
    //update closed pills
    setClosedPills([...closedPills, key]);

    //unselect if selected
    setSelectedKeys(selectedKeys?.filter((x) => x !== key) ?? []);

    props.pillCloseCallback?.(e);

    //to not call pill wrapper's on click select function after deletion
    e.stopPropagation();
  }
};

export default Checkbox;
